import { useCallback } from 'react';
import { db } from '../db';
import { MUTATION_HANDLERS, MUTATION_LAYER } from '../services/mutationHandlers';
import { generateMutationKey, MUTATION_STATUS } from '../services/mutationLog';
import { triggerDrain } from '../services/sync';
import { getOfflineToastNotifier } from './offlineToastRegistry';

const OFFLINE_TOAST_MESSAGE = "Saved — will sync when you're back online";
const OFFLINE_TOAST_DEBOUNCE_MS = 2000;

// Module-level debounce state, shared across every hook instance so a burst of
// parallel mutate() calls (e.g. mark-all-read fires one mark_read per article)
// produces a single toast, not N.
let offlineToastTimer = null;

// Trailing debounce: N offline commits within the window fire one toast.
function scheduleOfflineToast() {
  if (offlineToastTimer) clearTimeout(offlineToastTimer);
  offlineToastTimer = setTimeout(() => {
    offlineToastTimer = null;
    // Reconnected within the debounce window: the sync has already happened (or
    // is happening), so a "will sync when you're back online" toast would lie.
    const notify = getOfflineToastNotifier();
    if (navigator.onLine === false && notify) {
      notify(OFFLINE_TOAST_MESSAGE);
    }
  }, OFFLINE_TOAST_DEBOUNCE_MS);
}

/**
 * Local-first mutation hook.
 *
 * Returns `{ mutate }` where `mutate({ type, payload })`:
 *   1. Looks up the handler in MUTATION_HANDLERS (throws if unknown).
 *   2. Opens ONE Dexie 'rw' transaction over [mutation_log, …entity tables].
 *   3. For optimistic-write types, runs handler.applyOptimistic(tx, payload, key).
 *   4. Appends the row to mutation_log with status='pending'.
 *   5. Fires triggerDrain() fire-and-forget — never throws to caller.
 *   6. Re-throws if the transaction fails (caller can toast).
 *
 * Atomicity: if applyOptimistic throws, the log row is rolled back too.
 */
export function useLocalMutation() {
  const mutate = useCallback(async ({ type, payload }) => {
    const handler = MUTATION_HANDLERS[type];
    if (!handler) throw new Error(`Unknown mutation type: ${type}`);

    const key = generateMutationKey();
    const created_at = Date.now();

    // Declare all tables that may be written so Dexie can lock them atomically.
    // OVERLAY types only need mutation_log; OPTIMISTIC_WRITE types touch entity tables too.
    const tableNames =
      handler.layer === MUTATION_LAYER.OPTIMISTIC_WRITE
        ? ['mutation_log', 'articles', 'highlights']
        : ['mutation_log'];

    let priorState = null;
    await db.transaction('rw', tableNames.map((t) => db.table(t)), async (tx) => {
      if (handler.layer === MUTATION_LAYER.OPTIMISTIC_WRITE && handler.applyOptimistic) {
        const result = await handler.applyOptimistic(tx, payload, key);
        if (result?.prior_state) priorState = result.prior_state;
      }
      const entry = {
        key,
        type,
        payload,
        status: MUTATION_STATUS.PENDING,
        created_at,
        attempts: 0,
        ...(priorState ? { prior_state: priorState } : {}),
      };
      await tx.table('mutation_log').put(entry);
    });

    // Fire-and-forget; never throws to caller.
    try { triggerDrain(); } catch { /* swallow */ }

    // Reported-offline commits get a coalesced trust affordance. Never on flaky
    // online commits, and never for errors — those keep their existing surfaces.
    if (navigator.onLine === false) scheduleOfflineToast();

    return { key };
  }, []);

  return { mutate };
}

export default useLocalMutation;
